import { FormikProps, withFormik } from 'formik';
import * as React from 'react';
import { AnalyticsExtensionsSettingsStyles } from '../styles/AnalyticsExtensionsSettingsStyles';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import {
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelWidget,
  DialogType,
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  TextFieldWidget
} from '@tableau/widgets';
import { Messages } from '../../codegen/Localize';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { AnalyticsExtensionsSettingsCtrlProps } from '../controllers/SiteSettingsForAnalyticsExtensionsCtrl';
import { IAnalyticsExtConnectionSettings, IAnalyticsExtConnectionSettingsErrors, IAnalyticsExtSiteSettings } from '../types/ISiteSettingsForSiteAdmin';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { DialogErrorAlert } from '../../common/react/components/dialogAlerts/DialogAlerts';
import { FeatureFlags } from '../../ts/FeatureFlags';

export interface DialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  ctrlProps: AnalyticsExtensionsSettingsCtrlProps;
}

const MAX_HOSTNAME_LENGTH: number = 255;
const MIN_PORT_NUMBER: number = 1;
const MAX_PORT_NUMBER: number = 65535;
const MAX_USERNAME_LENGTH: number = 255;
const MAX_PASSWORD_LENGTH: number = 255;

const http: string = 'http://';
const https: string = 'https://';

export type AnalyticsExtensionsSettingsDialogProps =
  AnalyticsExtensionsSettingsCtrlProps & DialogProps;

export interface AnalyticsExtensionsSettingsForSiteState{
  //saveFormState: IResult;
  readonly busy: boolean;
  readonly error: boolean;
  isOnline: boolean;
  configSettings: IAnalyticsExtConnectionSettings;
  errorMessages: IAnalyticsExtConnectionSettingsErrors;
  protocol: string;

}
export class AnalyticsExtensionsSettingsDialog extends React.Component < DialogProps, AnalyticsExtensionsSettingsForSiteState> {

  readonly ConnectionTypes = {
    TABPY: {
      TAG: 'TABPY',
      DISPLAY: 'TabPy/' + Messages.analyticsExtension()
    },
    RSERVE: {
      TAG: 'RSERVE',
      DISPLAY: 'Rserve'
    },
    EINSTEIN: {
      TAG: 'EINSTEIN_ANALYTICS',
      DISPLAY: 'Einstein Discovery'
    },
    SELECT: Messages.select()
  };

  readonly connectionItems: ListItem[] = [
    {value: this.ConnectionTypes.SELECT},
    {value: this.ConnectionTypes.TABPY.DISPLAY},
    {value: this.ConnectionTypes.RSERVE.DISPLAY}
  ];

  readonly connectionItemsEA: ListItem[] = [
    {value: this.ConnectionTypes.SELECT},
    {value: this.ConnectionTypes.TABPY.DISPLAY},
    {value: this.ConnectionTypes.RSERVE.DISPLAY},
    {value: this.ConnectionTypes.EINSTEIN.DISPLAY}
  ];

  private originalSettings: IAnalyticsExtConnectionSettings;
  private initProtocol: string = this.initialProtocol();
  public constructor(props: DialogProps) {
    super(props);
    this.originalSettings =
      {...this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionDefault,
        is_ssl_enabled: props.ctrlProps.isOnline ? props.ctrlProps.isOnline : this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionDefault?.is_ssl_enabled,
        is_auth_enabled: props.ctrlProps.isOnline ? props.ctrlProps.isOnline : this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionDefault?.is_auth_enabled
      };
    this.state = {
      busy: false,
      error: false,
      isOnline: props.ctrlProps.isOnline,
      configSettings: this.originalSettings,
      errorMessages: {connection_luid: null, connection_type: null, host: null, port: null, username: null, password: null},
      protocol: this.initProtocol
    };
  }

  private initialProtocol() {
    let retVal = '';
    const connectionDisplayType = this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionDefault?.connection_type;
    if(connectionDisplayType === this.ConnectionTypes.TABPY.TAG) {
      const ssl =  this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionDefault.is_ssl_enabled;
      retVal = ssl ? https : http;
    }
    return retVal;
  }

  public updateProtocol() {
     if (this.state.configSettings.connection_type === this.ConnectionTypes.RSERVE.TAG){
       this.setState({protocol: ''});
     }else if (this.state.configSettings.is_ssl_enabled && this.state.configSettings.connection_type === this.ConnectionTypes.TABPY.TAG){
       this.setState({protocol: https});
     }else if (!this.state.configSettings.is_ssl_enabled && this.state.configSettings.connection_type === this.ConnectionTypes.TABPY.TAG){
       this.setState({protocol: http});
     }else{
       // no connection type selected yet
       this.setState({protocol: ''});
     }
  }

  public convertTypeDisplay(value: string): string {
    if (value === this.ConnectionTypes.RSERVE.TAG){
      return this.ConnectionTypes.RSERVE.DISPLAY;
    }else if (value === this.ConnectionTypes.TABPY.TAG){
      return this.ConnectionTypes.TABPY.DISPLAY;
    }else if (FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension && value === this.ConnectionTypes.EINSTEIN.TAG){
      return this.ConnectionTypes.EINSTEIN.DISPLAY;
    }else{
      return this.ConnectionTypes.SELECT;
    }
  }

  private clearLoginInfo() {
    this.handleChangeCallback('', 'username');
    this.handleChangeCallback('', 'password');
  }

  private removeProtocolFromHostUrl() {
    let settings = this.state.configSettings;
    if (settings.host.indexOf('://') !== -1) {
      settings.host = settings.host.substr(settings.host.lastIndexOf('://') + 3);
      this.setState({configSettings: settings});
    }
  }

  private validateInput(): boolean {
    let settings = this.state.configSettings;
    let errors = this.state.errorMessages;
    let isValid = true;
    if ((!settings.connection_type) || (settings.connection_type === this.ConnectionTypes.SELECT)) {
          errors.connection_type = 'Invalid';
          isValid = false;
    } else {
      errors.connection_type = null;
    }
    if ((!settings.host) || (settings.host.length > MAX_HOSTNAME_LENGTH)) {
      errors.host = 'Invalid';
      isValid = false;
    } else {
      errors.host = null;
    }

    if ((!settings.port) || (!Number.isInteger(+settings.port)) || (+settings.port < MIN_PORT_NUMBER) || (+settings.port > MAX_PORT_NUMBER)) {
      errors.port = 'Invalid';
      isValid= false;
    } else {
      errors.port = null;
    }

    if (settings.is_auth_enabled && settings.username && (settings.username.length > MAX_USERNAME_LENGTH)) {
      errors.username = 'Invalid';
      isValid = false;
    } else {
      errors.username = null;
    }

    if (settings.is_auth_enabled && settings.password && (settings.password.length > MAX_PASSWORD_LENGTH)) {
      errors.password = 'Invalid';
      isValid = false;
    } else {
      errors.password = null;
    }

    if (this.state.isOnline && !settings.password) {
      errors.password = 'Invalid';
      isValid = false;
    } else {
      errors.password = null;
    }

    if (this.state.isOnline && !settings.username) {
      errors.username = 'Invalid';
      isValid = false;
    } else {
      errors.username = null;
    }

    return isValid;
  }

  public handleChangeCallback = (value: any, fieldName: keyof IAnalyticsExtConnectionSettings) => {
    let updateSettings = this.state.configSettings;
    (updateSettings[fieldName] as any) = value;
    this.setState({configSettings:updateSettings});
    this.updateProtocol();
    this.removeProtocolFromHostUrl();
    this.validateInput();

  }

  public handleTypeChangeCallback = (value: any, fieldName: keyof IAnalyticsExtConnectionSettings) => {
    let updateSettings = this.state.configSettings;
    let valueTAG = value;
    if (value === this.ConnectionTypes.RSERVE.DISPLAY){
      valueTAG = this.ConnectionTypes.RSERVE.TAG;
    }
    else if(value === this.ConnectionTypes.TABPY.DISPLAY){
      valueTAG = this.ConnectionTypes.TABPY.TAG;
    }
    else if(value === this.ConnectionTypes.EINSTEIN.DISPLAY){
      valueTAG = this.ConnectionTypes.EINSTEIN.TAG;
    }
    (updateSettings[fieldName] as any) = valueTAG;
    this.setState({configSettings:updateSettings});
    this.updateProtocol();
    this.validateInput();
  }

  private handleCreate = () => {
    this.setState({busy: true});
    this.props.ctrlProps.sendUpdateConenctionRequest(this.state.configSettings).then(
      (result: IResult) => {
        if(!result || (result as any).errors){
          this.setState({error: true});
          return;
        }else{
          this.props.ctrlProps.initialAnalyticsExtensionsSettings.connectionDefault = result as IAnalyticsExtConnectionSettings;
          this.props.close(result);
        }
      }
    );
  }

  render() {
    const connectionDisplay = this.convertTypeDisplay(this.state.configSettings.connection_type);
    const errors = this.state.errorMessages;
    return(
      <section className={AnalyticsExtensionsSettingsStyles.editDialogSection}>
        {this.state.error && <DialogErrorAlert testId={'fail-save-alert'} message={Messages.settingsUpdateFailure()} />}
        <div className={AnalyticsExtensionsSettingsStyles.editDialogTitle}>
          {Messages.analyticsExtensionsSettingsEditDialogHeader()}
        </div>
        <div className={AnalyticsExtensionsSettingsStyles.editDialogDescription}>
          {Messages.analyticsExtensionsSettingsEditDialogDescription()}
        </div>
        <div className={AnalyticsExtensionsSettingsStyles.editDialogBlock}>
          <div className={AnalyticsExtensionsSettingsStyles.editDialogInputBlock}>
            <SingleSelectDropdown
              testId="anext_site_connection_dropdown_dialog"
              label={Messages.analyticsExtensionsSettingsConnectoinType()}
              list={<List items={FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension?this.connectionItemsEA:this.connectionItems} selectedValue={connectionDisplay}/>}
              onChange={(connectionType: string) => {
                this.handleTypeChangeCallback(connectionType, 'connection_type');}
              }
              style={{ width: 180 }}
              type={DropdownButtonType.Line}
              disabled={false}
              value={connectionDisplay}
              valid={errors.connection_type ? false : undefined}
            />
          </div>
        </div>
        <div className={AnalyticsExtensionsSettingsStyles.editDialogBlock}>
          <div className={AnalyticsExtensionsSettingsStyles.editDialogInputBlock}>
            <CheckBoxWithLabelWidget
              checked={this.state.configSettings.is_ssl_enabled}
              disabled={this.state.isOnline}
              handleChange={(isBoxChecked: boolean) => {
                this.handleChangeCallback(isBoxChecked, 'is_ssl_enabled');
                this.clearLoginInfo();
              }}
              testId="anext_site_secure_checkbox_dialog"
              label={Messages.ssl()}
              containerStyle={AnalyticsExtensionsSettingsStyles.connectionSslCheckbox}
            />
          </div>
          <div className={this.state.configSettings.connection_type === this.ConnectionTypes.TABPY.TAG ?
                             AnalyticsExtensionsSettingsStyles.editDialogInputInline : AnalyticsExtensionsSettingsStyles.Hide}>
            <div className={AnalyticsExtensionsSettingsStyles.editDialogProtocolLabel}>{this.state.protocol}</div>
          </div>
          <div className={AnalyticsExtensionsSettingsStyles.editDialogInputInline}>
          <TextFieldWidget
            testId="anext_site_host_textfield_dialog"
            label={Messages.host()}
            onChange={e => {
              this.handleChangeCallback(e.target.value, 'host');
              this.clearLoginInfo();
              }
            }
            disabled={this.state.configSettings.connection_type === this.ConnectionTypes.SELECT}
            value={this.state.configSettings.host}
            valid={errors.host ? false : undefined}
            className={this.state.configSettings.connection_type === this.ConnectionTypes.TABPY.TAG ?
                        AnalyticsExtensionsSettingsStyles.editDialogTabPyHostInput : AnalyticsExtensionsSettingsStyles.editDialogHostInput}
          />
          </div>
          <div className={AnalyticsExtensionsSettingsStyles.editDialogInputInline}>
            <TextFieldWidget
              testId="anext_site_port_textfield_dialog"
              label={Messages.port()}
              onChange={e => {
                this.handleChangeCallback(e.target.value, 'port');
                this.clearLoginInfo();
                }
              }
              disabled={this.state.configSettings.connection_type === this.ConnectionTypes.SELECT}
              value={this.state.configSettings.port}
              valid={errors.port ? false : undefined}
              className={AnalyticsExtensionsSettingsStyles.editDialogPortInput}
            />
          </div>
        </div>
        <div className={AnalyticsExtensionsSettingsStyles.editDialogBlock}>
          <div className={AnalyticsExtensionsSettingsStyles.editDialogInputBlock}>
            <CheckBoxWithLabelWidget
              checked={this.state.configSettings.is_auth_enabled}
              disabled={this.state.isOnline}
              handleChange={(isBoxChecked: boolean) => {
                this.handleChangeCallback(isBoxChecked, 'is_auth_enabled');
                this.clearLoginInfo();
                }}
              testId="anext_site_auth_checkbox_dialog"
              label={Messages.analyticsExtensionsSettingsAuthentication()}
            />
          </div>
          <div className={AnalyticsExtensionsSettingsStyles.editDialogInputInline}>
            <TextFieldWidget
              testId="anext_site_userName_textfield_dialog"
              label={Messages.username()}
              onChange={e => {this.handleChangeCallback(e.target.value, 'username');}}
              disabled={(this.state.configSettings.connection_type === this.ConnectionTypes.SELECT) ||
                        (!this.state.configSettings.is_auth_enabled)}
              value={this.state.configSettings.username}
              valid={errors.username ? false : undefined}
              className={AnalyticsExtensionsSettingsStyles.editDialogUserNameInput}
            />
          </div>
          <div className={AnalyticsExtensionsSettingsStyles.editDialogInputInline}>
            <TextFieldWidget
              testId="anext_site_password_textfield_dialog"
              label={Messages.password()}
              onChange={e => {this.handleChangeCallback(e.target.value, 'password');}}
              disabled={(this.state.configSettings.connection_type === this.ConnectionTypes.SELECT) ||
                        (!this.state.configSettings.is_auth_enabled)}
              value={this.state.configSettings.password}
              valid={errors.password ? false : undefined}
              type={'password'}
              className={AnalyticsExtensionsSettingsStyles.editDialogPasswordInput}
            />
          </div>
        </div>

        <div className={AnalyticsExtensionsSettingsStyles.editDialogBlock}>
          <div className={AnalyticsExtensionsSettingsStyles.editDialogActions}>
            <div className={AnalyticsExtensionsSettingsStyles.editDialogCancel}>
              <ButtonWithSpinner
                buttonType={ButtonType.Outline}
                showSpinner={false}
                clicked={this.props.dismiss}
                testId={'dialog-cancel'}
                title={Messages.cancel()}
                disabled={this.state.busy}
              />
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.editDialogSave}>
              <ButtonWithSpinner
                buttonType={ButtonType.Go}
                showSpinner={this.state.busy}
                clicked={this.handleCreate}
                testId={'dialog-save'}
                title={Messages.save()}
                disabled={this.state.busy || !this.validateInput()}
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

}



